import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

// Foto real de Francisco Gonzalez Trejo
const VET_IMAGE = '/assets/fran-veterinario.webp';

const approaches = [
  {
    image: '/assets/about/formacion.webp',
    title: 'Formación continua',
    description: 'Actualización constante en nutrición animal.',
  },
  {
    image: '/assets/about/plan.webp',
    title: 'Personalización real',
    description: 'Cada plan es único, como cada animal.',
  },
  {
    image: '/assets/about/seguridad.webp',
    title: 'Transparencia',
    description: 'Sin promesas milagro. Honestidad ante todo.',
  },
];

export const AboutSection = () => {
  return (
    <section className="py-20 md:py-28 bg-brand-sage/10" data-testid="about-section">
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-brand-lg">
              <img
                src={VET_IMAGE}
                alt="Veterinario examinando mascota"
                className="w-full h-auto aspect-[4/3] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/20 to-transparent" />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-6 -right-6 md:right-10 bg-white rounded-2xl shadow-brand p-4">
              <p className="font-caveat text-brand-primary text-lg">100% Online</p>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-manrope text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary tracking-tight mb-6 section-title">
              Mi enfoque
            </h2>
            
            <p className="text-text-secondary text-lg mb-8 leading-relaxed">
              Creo en una nutrición basada en alimentos reales, adaptada a cada animal y respaldada 
              por conocimiento actualizado. Mi objetivo no es vender una dieta milagrosa, sino ayudarte 
              a entender qué necesita tu perro o gato y cómo implementarlo de forma práctica.
            </p>

            <div className="space-y-4 mb-8">
              {approaches.map((approach, index) => (
                <div 
                  key={index}
                  className="group cursor-pointer flex items-start gap-4 bg-white rounded-xl p-3 
                           border border-gray-100 transition-all duration-300 ease-out
                           hover:border-[#3D6B4F] hover:shadow-lg hover:shadow-[#3D6B4F]/10
                           active:bg-[#3D6B4F] active:border-[#3D6B4F]"
                >
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-[#EDF2EA] p-2 flex-shrink-0
                                transition-all duration-300 group-hover:bg-[#E8C84B]/20 group-active:bg-white/20">
                    <img 
                      src={approach.image} 
                      alt={approach.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h4 className="font-manrope font-bold text-text-primary
                                 transition-colors duration-300 group-hover:text-[#3D6B4F] group-active:text-white">
                      {approach.title}
                    </h4>
                    <p className="text-text-secondary text-sm transition-colors duration-300 group-active:text-white/80">
                      {approach.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/sobre-realimenta"
              className="inline-flex items-center gap-2 text-brand-primary font-semibold group"
              data-testid="about-cta"
            >
              Conoce más sobre REALimenta
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
